import type { AutomationRule } from '@servicetim/shared';
import {
  useAutomationRules,
  useDeleteAutomationRule,
  useProjects,
  useUpdateAutomationRule,
} from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  DropdownMenuItem,
  DropdownMenuSeparator,
  pagedTableProps,
  Switch,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { formatDateTime } from '../tenant/common/format';
import { RowActions } from '../tenant/common/row-actions';
import { problemMessage, withProblemMessage } from '../tenant/common/server-errors';
import { RuleEditorSheet } from './rule-editor-sheet';
import { triggerSummary } from './trigger-summary';

type Dialog = { kind: 'create' } | { kind: 'edit'; rule: AutomationRule } | null;

export interface AutomationsTableProps {
  /** Filters the list to one project's rules; when unset every rule the caller can see. */
  filterProjectId?: string;
  /** Locks new rules to this project (the project's "Automations" tab); unset on the tenant page. */
  lockedProjectId?: string;
}

/** List of automation rules with enable/disable, edit and delete (S-07-01 … S-07-04). */
export function AutomationsTable({ filterProjectId, lockedProjectId }: AutomationsTableProps) {
  const rules = useAutomationRules({ projectId: filterProjectId });
  const projects = useProjects({ pageSize: 200 });
  const update = useUpdateAutomationRule();
  const remove = useDeleteAutomationRule();
  const confirm = useConfirm();
  const [dialog, setDialog] = useState<Dialog>(null);

  const projectLabel = useCallback(
    (projectId: string | null) => {
      if (!projectId) return 'Tenant-wide';
      const project = projects.items.find((p) => p.id === projectId);
      return project ? `${project.key} · ${project.name}` : projectId.slice(0, 8);
    },
    [projects.items],
  );

  const onDelete = useCallback(
    (rule: AutomationRule) =>
      void confirm({
        title: `Delete "${rule.name}"?`,
        description: 'The rule stops firing; its run history is kept.',
        confirmLabel: 'Delete',
        destructive: true,
        onConfirm: async () => {
          await withProblemMessage(() => remove.mutateAsync(rule.id));
          toast.success(`"${rule.name}" deleted`);
        },
      }),
    [confirm, remove.mutateAsync],
  );

  const toggleEnabled = useCallback(
    async (rule: AutomationRule, enabled: boolean) => {
      try {
        await update.mutateAsync({ id: rule.id, patch: { enabled } });
        toast.success(`"${rule.name}" ${enabled ? 'enabled' : 'disabled'}`);
      } catch (error) {
        toast.error(problemMessage(error));
      }
    },
    [update.mutateAsync],
  );

  const columns = useMemo<ColumnDef<AutomationRule, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="font-medium text-fg">{row.original.name}</span>
            {row.original.isSystem ? <Badge variant="secondary">System</Badge> : null}
          </div>
        ),
      },
      {
        id: 'trigger',
        header: 'Trigger',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-fg-muted text-sm">{triggerSummary(row.original.trigger)}</span>
        ),
      },
      ...(filterProjectId
        ? []
        : [
            {
              id: 'project',
              header: 'Project',
              enableSorting: false,
              cell: ({ row }: { row: { original: AutomationRule } }) => (
                <span className="text-fg-muted text-sm">
                  {projectLabel(row.original.projectId)}
                </span>
              ),
            } satisfies ColumnDef<AutomationRule, unknown>,
          ]),
      {
        accessorKey: 'enabled',
        header: 'Enabled',
        cell: ({ row }) => (
          <Switch
            checked={row.original.enabled}
            aria-label={`${row.original.name} enabled`}
            disabled={update.isPending}
            onCheckedChange={(checked) => void toggleEnabled(row.original, checked)}
          />
        ),
      },
      {
        accessorKey: 'lastRunAt',
        header: 'Last run',
        cell: ({ row }) =>
          row.original.lastRunAt ? (
            <span className="text-fg-muted">{formatDateTime(row.original.lastRunAt)}</span>
          ) : (
            <span className="text-fg-muted">Never</span>
          ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        size: 48,
        cell: ({ row }) => (
          <RowActions label={`Actions for ${row.original.name}`}>
            <DropdownMenuItem onSelect={() => setDialog({ kind: 'edit', rule: row.original })}>
              <Pencil /> {row.original.isSystem ? 'View' : 'Edit'}
            </DropdownMenuItem>
            {row.original.isSystem ? null : (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => onDelete(row.original)}>
                  <Trash2 /> Delete
                </DropdownMenuItem>
              </>
            )}
          </RowActions>
        ),
      },
    ],
    [filterProjectId, projectLabel, update.isPending, onDelete, toggleEnabled],
  );

  return (
    <>
      <DataTable
        columns={columns}
        {...pagedTableProps(rules)}
        getRowId={(r) => r.id}
        caption="Automation rules"
        onRowClick={(rule) => setDialog({ kind: 'edit', rule })}
        toolbar={
          <div className="flex justify-end">
            <Button onClick={() => setDialog({ kind: 'create' })}>
              <Plus /> New rule
            </Button>
          </div>
        }
        emptyTitle="No automation rules"
        emptyDescription="Trigger, conditions and actions run as declarative JSON — no scripting."
        emptyAction={
          <Button onClick={() => setDialog({ kind: 'create' })}>
            <Plus /> New rule
          </Button>
        }
      />
      <RuleEditorSheet
        rule={dialog?.kind === 'create' ? null : dialog?.kind === 'edit' ? dialog.rule : undefined}
        lockedProjectId={lockedProjectId}
        onOpenChange={(open) => (open ? undefined : setDialog(null))}
      />
    </>
  );
}
